import { inject } from '@angular/core';
import { Router, type CanActivateFn, type UrlTree } from '@angular/router';

import { AuthService } from '../services/auth.service';
import { LOGIN_ROUTE_POLICY } from '../config/login-route-policy.token';

/**
 * Guard that returns true if the user is authenticated.
 * Redirects to the configured loginRoute when unauthenticated.
 */
export const authGuard: CanActivateFn = (_route, state): boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const { loginRoute } = inject(LOGIN_ROUTE_POLICY);

  if (authService.isAuthenticated()) {
    return true;
  }

  return router.parseUrl(`${loginRoute}?returnUrl=${encodeURIComponent(state.url)}`);
};

/**
 * Guard that returns true if the user is NOT authenticated (guest).
 * Redirects to the default route if already authenticated.
 */
export const guestGuard: CanActivateFn = (_route, _state): boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const { defaultRoute } = inject(LOGIN_ROUTE_POLICY);

  if (!authService.isAuthenticated()) {
    return true;
  }

  return router.parseUrl(defaultRoute);
};

/**
 * Guard that returns true if the user has admin role.
 * Redirects unauthenticated users to loginRoute.
 */
export const adminGuard: CanActivateFn = (_route, state): boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const { loginRoute } = inject(LOGIN_ROUTE_POLICY);

  if (!authService.isAuthenticated()) {
    return router.parseUrl(`${loginRoute}?returnUrl=${encodeURIComponent(state.url)}`);
  }

  if (authService.isAdmin()) {
    return true;
  }

  return router.parseUrl('/unauthorized');
};

/**
 * Guard that returns true if the user has host or admin role.
 * Redirects unauthenticated users to loginRoute.
 */
export const hostGuard: CanActivateFn = (_route, state): boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const { loginRoute } = inject(LOGIN_ROUTE_POLICY);

  if (!authService.isAuthenticated()) {
    return router.parseUrl(`${loginRoute}?returnUrl=${encodeURIComponent(state.url)}`);
  }

  if (authService.isHost() || authService.isAdmin()) {
    return true;
  }

  return router.parseUrl('/unauthorized');
};

/**
 * Guard that returns true if the user is a game organizer or admin.
 * Redirects unauthenticated users to loginRoute.
 */
export const gameOrganizerGuard: CanActivateFn = (_route, state): boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const { loginRoute } = inject(LOGIN_ROUTE_POLICY);

  if (!authService.isAuthenticated()) {
    return router.parseUrl(`${loginRoute}?returnUrl=${encodeURIComponent(state.url)}`);
  }

  if (authService.isGameOrganizerRole() || authService.isAdmin()) {
    return true;
  }

  return router.parseUrl('/unauthorized');
};

/**
 * Guard that returns true if the user is a business owner or admin.
 * Redirects unauthenticated users to loginRoute.
 */
export const businessOwnerGuard: CanActivateFn = (_route, state): boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const { loginRoute } = inject(LOGIN_ROUTE_POLICY);

  if (!authService.isAuthenticated()) {
    return router.parseUrl(`${loginRoute}?returnUrl=${encodeURIComponent(state.url)}`);
  }

  if (authService.isBusinessOwnerRole() || authService.isAdmin()) {
    return true;
  }

  return router.parseUrl('/unauthorized');
};
